/**
 * FirebaseConfigProvider - Config provider for hosted mode
 *
 * Fetches sequence configuration from Firestore.
 * Handles password protection and URL parsing.
 */

import { Logger } from '../../utils/Logger';
import type { FirebaseApp } from 'firebase/app';

const logger = Logger.getLogger('FirebaseConfigProvider');
import type { IConfigProvider, LoadedConfig, SequenceMetadata } from '../interfaces';
import type { ExperienceConfig } from '../../types/config';
import {
  parseSequencePath,
  getSequence,
  requiresPassword as checkRequiresPassword,
  verifySequencePassword,
} from '../../api/SequenceAPI';
import { getUserProfile, TIER_CONFIG } from '../../api/UserProfileAPI';
import { getSequenceByGlobalSlug, getSequenceByUserSlug } from '../../api/SlugAPI';
import { getUserIdByUsername } from '../../api/UsernameAPI';
import { resolveSequenceIdFromPathname } from './pathResolver';

/**
 * Check if a URL points to our hosted storage (GCS or Firebase Storage)
 * This distinguishes between self-hosted tours (external URLs) and uploaded tours (our storage)
 *
 * Works in both hosted mode (with env vars) and export mode (without env vars)
 */
function isHostedUrl(url: string | undefined): boolean {
  if (!url) return false;

  // Try to get our GCS bucket URL from environment
  // In export mode, this might be undefined or empty
  try {
    const gcsBucketUrl = import.meta.env?.VITE_GCS_BUCKET_URL || '';
    if (gcsBucketUrl) {
      // Extract domain from bucket URL for matching
      const bucketDomain = gcsBucketUrl.replace('https://', '').replace('http://', '').split('/')[0];
      if (url.includes(bucketDomain)) {
        return true;
      }
    }
  } catch {
    // Environment variables not available (export mode)
  }

  // Check for common Firebase/GCS storage patterns that indicate hosted content
  // These patterns work regardless of environment configuration
  const hostedPatterns = [
    'storage.googleapis.com/webxrgallery',       // Our GCS bucket (production)
    'storage.googleapis.com/xrgallery',          // Alternative bucket name
    'firebasestorage.googleapis.com/v0/b/webxrgallery', // Firebase Storage for our project
    'webxrgallery.appspot.com',                  // Firebase Storage alternative domain
  ];

  return hostedPatterns.some(pattern => url.includes(pattern));
}

/**
 * Scan an ExperienceConfig to determine if it contains any hosted media
 * Returns true if any URLs point to our storage (uploaded content)
 * Returns false if all URLs are external (self-hosted content)
 */
function configHasHostedContent(config: ExperienceConfig): boolean {
  if (!config?.stages) return false;

  for (const stage of config.stages) {
    // Check skybox
    if (stage.skybox) {
      if (isHostedUrl(stage.skybox.url)) return true;

      // Check skybox variants
      if (stage.skybox.variants) {
        const variants = stage.skybox.variants;
        if (isHostedUrl(variants.desktop?.url)) return true;
        if (isHostedUrl(variants.mobile?.url)) return true;
        if (isHostedUrl(variants.tablet?.url)) return true;
        if (isHostedUrl(variants.vr?.url)) return true;
        if (isHostedUrl(variants.fallback?.url)) return true;
      }
    }

    // Check audio
    if (isHostedUrl(stage.audioUrl)) return true;

    // Check models
    if (stage.models) {
      for (const model of stage.models) {
        if (isHostedUrl(model.url)) return true;
      }
    }

    // Check planes
    if (stage.planes) {
      for (const plane of stage.planes) {
        if (isHostedUrl(plane.url)) return true;
        if (isHostedUrl(plane.hlsUrl)) return true;
      }
    }

    // Check hotspots
    if (stage.hotspots) {
      for (const hotspot of stage.hotspots) {
        if (isHostedUrl(hotspot.customIconUrl)) return true;
        if (isHostedUrl(hotspot.infoImageUrl)) return true;
        if (isHostedUrl(hotspot.videoUrl)) return true;
        if (isHostedUrl(hotspot.audioUrl)) return true;
        if (isHostedUrl(hotspot.iframeUrl)) return true;
      }
    }
  }

  // Check navigation floor plan
  if (config.navigation?.floorPlan) {
    if (isHostedUrl(config.navigation.floorPlan.imageUrl)) return true;
  }

  return false;
}

export class FirebaseConfigProvider implements IConfigProvider {
  private sequenceId: string | null;
  private cachedConfig: LoadedConfig | null = null;

  /**
   * Create a Firebase config provider
   * @param _app Firebase app instance (Firestore is accessed via SequenceAPI)
   * @param sequenceId Sequence ID to load (can also be parsed from URL)
   */
  constructor(_app: FirebaseApp, sequenceId?: string) {
    // Note: We don't need to store the app or db here because
    // SequenceAPI already has access to Firestore via its own imports
    this.sequenceId = sequenceId || this.parseSequenceFromURL();
  }

  /**
   * Parse sequence ID from current URL
   */
  private parseSequenceFromURL(): string | null {
    const currentPath = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);

    // Check for sequence ID in query params first (for viewer.html?id=xxx)
    const queryId = searchParams.get('id');
    if (queryId) {
      return queryId;
    }

    // Check if URL contains a sequence ID (/{sequenceId})
    return parseSequencePath(currentPath);
  }

  /**
   * Check if this is a demo request
   */
  private isDemoMode(): boolean {
    const searchParams = new URLSearchParams(window.location.search);
    const currentPath = window.location.pathname;
    return searchParams.get('demo') === 'true' || currentPath === '/demo';
  }

  /**
   * Resolve custom URL paths (slug routes) to a concrete sequence ID.
   * This is primarily used in local/dev where server-side rewrites may not run.
   */
  private async resolveSequenceIdFromPath(): Promise<string | null> {
    return resolveSequenceIdFromPathname(window.location.pathname, {
      async resolveGlobalSlug(slug: string) {
        const result = await getSequenceByGlobalSlug(slug);
        return result?.sequenceId ?? null;
      },
      resolveUsernameToUserId: getUserIdByUsername,
      resolveUserSlug: getSequenceByUserSlug,
    });
  }

  /**
   * Load configuration from Firestore
   */
  async loadConfig(): Promise<LoadedConfig> {
    // Return cached config if available
    if (this.cachedConfig) {
      return this.cachedConfig;
    }

    // Handle demo mode
    if (this.isDemoMode()) {
      logger.debug('Demo mode - loading default config');
      // In demo mode, return empty config (UIManager will load from /config.json)
      const response = await fetch('/config.json');
      const config = await response.json();
      this.cachedConfig = { config };
      return this.cachedConfig;
    }

    if (!this.sequenceId) {
      this.sequenceId = await this.resolveSequenceIdFromPath();
    }

    if (!this.sequenceId) {
      throw new Error('No sequence ID provided and none found in URL');
    }

    logger.info(`Loading sequence: ${this.sequenceId}`);

    let sequence = await getSequence(this.sequenceId);

    // Fallback for slug-style URLs in local/dev:
    // parseSequencePath('/my-tour') returns "my-tour" (slug),
    // but getSequence expects a Firestore document ID.
    if (!sequence) {
      const resolvedSequenceId = await this.resolveSequenceIdFromPath();
      if (resolvedSequenceId && resolvedSequenceId !== this.sequenceId) {
        this.sequenceId = resolvedSequenceId;
        logger.info(`Resolved custom URL to sequence ID: ${this.sequenceId}`);
        sequence = await getSequence(this.sequenceId);
      }
    }

    if (!sequence) {
      throw new Error(`Sequence not found: ${this.sequenceId}`);
    }

    logger.info('Sequence loaded:', sequence.name);

    // Check if the sequence uses hosted content (files on our storage)
    // Self-hosted tours (all external URLs) don't require an active subscription
    const hasHostedContent = configHasHostedContent(sequence.config as ExperienceConfig);

    if (hasHostedContent) {
      logger.debug('Tour has hosted content, checking subscription...');

      // Check if the sequence owner has an active subscription for hosted tours
      //
      // IMPORTANT: We check subscriptionStatus, NOT tier, because:
      // - When a subscription lapses, tier gets set to 'free' but subscriptionStatus = 'canceled'
      // - A true free user has subscriptionStatus = 'none' or undefined
      // - A lapsed paid user has subscriptionStatus = 'canceled' (their hosted tours should be blocked)
      //
      // Owner can still:
      // - Access their dashboard and preview tours there (doesn't use this code path)
      // - Download/export tours for self-hosting
      // - Reactivate subscription to restore public access
      try {
        const ownerProfile = await getUserProfile(sequence.userId);
        if (ownerProfile) {
          const tier = ownerProfile.tier;
          const subscriptionStatus = ownerProfile.subscriptionStatus;

          // Admin and enterprise tiers are always exempt (custom contracts, internal use)
          if (tier === 'admin' || tier === 'enterprise') {
            logger.debug('Admin/enterprise tier - allowing access');
          } else {
            // For all other users, check subscription status directly
            // 'canceled' = subscription was active but ended
            // 'past_due' = payment failed, subscription pending cancellation
            // These states mean the user HAD a subscription but it lapsed
            if (subscriptionStatus === 'canceled' || subscriptionStatus === 'past_due') {
              logger.warn('Subscription lapsed for tour owner:', sequence.userId, 'status:', subscriptionStatus);
              const error = Object.assign(new Error('Subscription inactive'), { code: 'subscription-inactive' });
              throw error;
            }

            // Allow access for:
            // - 'active': Currently paying
            // - 'trialing': In trial period
            // - 'none' or undefined: Never had a subscription (shouldn't have hosted content, but allow as fallback)
          }

          // Check bandwidth limits for paid users
          const tierConfig = TIER_CONFIG[tier] || TIER_CONFIG.free;
          const bandwidthLimit = tierConfig.bandwidthLimit;
          const bandwidthUsed = ownerProfile.bandwidthUsed || 0;
          const limitBehavior = ownerProfile.bandwidthLimitBehavior || 'soft'; // Default to soft (allow overages)

          // Only enforce limits for tiers with defined bandwidth limits (not unlimited)
          if (bandwidthLimit > 0 && bandwidthUsed > bandwidthLimit) {
            logger.warn(`Bandwidth limit exceeded for ${sequence.userId}: ${bandwidthUsed} / ${bandwidthLimit} bytes`);

            // If user chose 'hard' limit, block the tour
            if (limitBehavior === 'hard') {
              logger.warn('Hard limit - blocking tour access');
              const error = Object.assign(new Error('Bandwidth limit exceeded'), { code: 'bandwidth-exceeded' });
              throw error;
            } else {
              // Soft limit - allow with overage (will be billed)
              logger.debug('Soft limit - allowing with overage billing');
            }
          }
        }
      } catch (error) {
        // Re-throw subscription-inactive and bandwidth-exceeded errors, ignore other profile fetch errors
        const appError = error as { code?: string };
        if (appError?.code === 'subscription-inactive' || appError?.code === 'bandwidth-exceeded') {
          throw error;
        }
        logger.warn('Could not verify owner subscription, allowing access:', error);
      }
    } else {
      logger.debug('Tour is self-hosted (no hosted content), allowing access');
    }

    // Build metadata from sequence
    const metadata: SequenceMetadata = {
      sequenceId: this.sequenceId,
      name: sequence.name,
      userId: sequence.userId,
      isPublic: sequence.isPublic,
      hasPassword: !!sequence.password,
      orgId: sequence.orgId,
      logoUrl: sequence.logoUrl,
      removeBranding: sequence.removeBranding,
      removeFooterBranding: sequence.removeFooterBranding,
      analyticsEnabled: sequence.analyticsEnabled !== false, // Default to true
      disableTransitions: sequence.disableTransitions || false,
      disableGazeNavigation: sequence.disableGazeNavigation || false,
    };

    // Initialize bandwidth tracking for hosted content
    // This tracks resources loaded and reports to the tour owner's quota
    if (hasHostedContent && sequence.userId) {
      try {
        const { initBandwidthTracking } = await import('../BandwidthTracker');
        initBandwidthTracking(this.sequenceId, sequence.userId);
        logger.debug('Bandwidth tracking initialized for owner:', sequence.userId);
      } catch (error) {
        logger.warn('Failed to initialize bandwidth tracking:', error);
      }
    }

    // Merge portalSettings from sequence into config (if present)
    // portalSettings is stored at the sequence level for editor UI but belongs in ExperienceConfig for viewer
    const config = sequence.config as ExperienceConfig;
    if (sequence.portalSettings) {
      config.portalSettings = sequence.portalSettings;
    }

    this.cachedConfig = {
      config,
      metadata,
    };

    return this.cachedConfig;
  }

  /**
   * Check if password is required for this sequence
   */
  async requiresPassword(): Promise<boolean> {
    if (!this.sequenceId || this.isDemoMode()) {
      return false;
    }

    // Check session storage for already unlocked sequences
    const unlockedSequences = JSON.parse(
      sessionStorage.getItem('unlockedSequences') || '[]'
    );
    if (unlockedSequences.includes(this.sequenceId)) {
      return false;
    }

    return checkRequiresPassword(this.sequenceId);
  }

  /**
   * Verify password for this sequence
   */
  async verifyPassword(password: string): Promise<boolean> {
    if (!this.sequenceId) {
      return false;
    }

    const isValid = await verifySequencePassword(this.sequenceId, password);

    if (isValid) {
      // Store in session storage so user doesn't have to re-enter
      const unlockedSequences = JSON.parse(
        sessionStorage.getItem('unlockedSequences') || '[]'
      );
      unlockedSequences.push(this.sequenceId);
      sessionStorage.setItem('unlockedSequences', JSON.stringify(unlockedSequences));
    }

    return isValid;
  }

  /**
   * Get the sequence ID
   */
  getSequenceId(): string | null {
    return this.sequenceId;
  }
}
